import type { SupabaseClient } from '../supabase/client';
import type { Store, PublishStoreInput, PublishStoreResult, PublishErrorKey } from '../types/store';

/**
 * Publish the store. Calls the `publish_store(uuid, true)` RPC which
 * atomically:
 *
 *   1. verifies `auth.uid() IS NOT NULL`                           (42501 otherwise)
 *   2. verifies `is_store_admin(storeId)` — AC11                   (42501 otherwise)
 *   3. verifies the store exists & is not soft-deleted — M2        (P0002 otherwise)
 *   4. counts live products with inventory > 0 — AC2               (P0001 otherwise)
 *   5. flips `stores.published = true`
 *   6. flips `products.published = true` for every live product
 *   7. SELECTs and returns the updated stores row
 *
 * All in one transaction. Throws on any failure; the caller (hook or
 * dialog) maps the error via {@link mapPublishError}.
 *
 * The returned row matches the `Store` shape exactly, so the admin
 * dashboard hook can write it straight into
 * `queryClient.setQueryData(['store', storeId], result)` without
 * shape-drift — see F-004 §5.3 M1 load-bearing contract.
 */
export async function publishStore(
  client: SupabaseClient,
  input: PublishStoreInput,
): Promise<PublishStoreResult> {
  return invokePublishRpc(client, input.storeId, true);
}

/**
 * Unpublish the store. Calls `publish_store(uuid, false)`. Children
 * products are intentionally NOT touched (F-004 Decision 10 step 4b)
 * so a subsequent re-publish is clean.
 */
export async function unpublishStore(
  client: SupabaseClient,
  input: PublishStoreInput,
): Promise<PublishStoreResult> {
  return invokePublishRpc(client, input.storeId, false);
}

/**
 * Shared RPC-invocation helper for {@link publishStore} and
 * {@link unpublishStore}. Kept private to this module.
 *
 * The RPC returns the updated `stores` row. Supabase JS maps RETURNS
 * public.stores to a single-row object on success — no unwrap needed.
 */
async function invokePublishRpc(
  client: SupabaseClient,
  storeId: string,
  publish: boolean,
): Promise<Store> {
  const { data, error } = await client.rpc('publish_store', {
    target_store_id: storeId,
    publish,
  });
  if (error) throw error;
  if (!data) {
    // Defensive — the RPC RETURNS public.stores so `data` is always a
    // row on success. If we ever reach this branch the server contract
    // changed under us; surface a clear error rather than cast null.
    throw new Error('[publishStore] RPC returned no row');
  }
  return data as Store;
}

/**
 * Whitelist error-to-translation-key mapper. Code-first, no regex
 * fallbacks (F-004 §5.2 / Rev 2 L2): the RPC always attaches a
 * well-defined SQLSTATE, so we key solely on `err.code` and on the two
 * JS-side network failure shapes.
 *
 * Branches:
 *   1. null / undefined / non-object          → 'unknown'
 *   2. TypeError with /fetch/i message        → 'networkFailed'
 *   3. name === 'AuthRetryableFetchError'     → 'networkFailed'
 *   4. code === '42501'                       → 'permissionDenied'
 *   5. code === 'P0001' (inventory gate)      → 'noStockedProducts'
 *   6. code === 'P0003' (contact gate, F-007) → 'missingContactMethod'
 *   7. code === 'P1440' (logo gate)            → 'missingLogo'
 *   8. code === 'P0601' (billing required)     → 'subscriptionRequired'
 *   9. code === 'P0606' (product limit)        → 'activeProductLimitReached'
 *   10. code === 'P0002' (store not found/M2)  → 'storeNotFound'
 *   11. anything else                          → 'unknown'
 *
 * The `noStockedProducts` branch uses SQLSTATE P0001 only — the RPC's
 * canonical message is diagnostic, never authoritative. If a future
 * migration changes the message wording, this mapper keeps working.
 *
 * The `copyFailed` key in `PublishErrorKey` is never returned from this
 * mapper — it is surfaced directly by the copy-to-clipboard hook on
 * clipboard-API failure. Kept in the union so the dialog can render a
 * uniform error banner regardless of origin.
 */
export function mapPublishError(err: unknown): PublishErrorKey {
  // Branch 1: null / undefined / non-object.
  if (!err || typeof err !== 'object') {
    return 'common.errors.unknown';
  }

  // Branch 2: TypeError('fetch') — thrown by the browser when the
  // network is unreachable mid-request.
  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'common.errors.networkFailed';
  }

  const maybe = err as {
    code?: string;
    message?: string;
    name?: string;
  };

  // Branch 3: supabase-js auth retry exhaustion.
  if (maybe.name === 'AuthRetryableFetchError') {
    return 'common.errors.networkFailed';
  }

  // Branches 4-6: SQLSTATE codes from the RPC. Order is intentional —
  // the most specific (P0001, P0002) are below the most general
  // permission-denied bucket but above the catch-all.
  if (maybe.code === '42501') {
    return 'common.errors.permissionDenied';
  }

  if (maybe.code === 'P0001') {
    return 'admin.publishStore.errors.noStockedProducts';
  }

  // F-007 — the RPC's contact-method gate raises a distinct SQLSTATE
  // (P0003) specifically so this mapper can key on code alone, without
  // regex-matching `err.message`. Preserves F-004's "no regex fallbacks"
  // invariant (design §2.4 + §12.2).
  if (maybe.code === 'P0003') {
    return 'admin.publishStore.errors.missingContactMethod';
  }

  if (maybe.code === 'P1440') {
    return 'admin.publishStore.errors.missingLogo';
  }

  if (maybe.code === 'P0601') {
    return 'admin.publishStore.errors.subscriptionRequired';
  }

  if (maybe.code === 'P0606') {
    return 'admin.publishStore.errors.activeProductLimitReached';
  }

  if (maybe.code === 'P0002') {
    return 'common.errors.storeNotFound';
  }

  // Branch 7: fall-through.
  return 'common.errors.unknown';
}
